import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { BlogTableOfContents } from "@/components/BlogTableOfContents";
import { BlogReadingProgress } from "@/components/BlogReadingProgress";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, Brain, Target, Zap, Heart, Timer, CheckCircle } from "lucide-react";
import heroImage from "@/assets/blog/stop-procrastination-hero.jpg";
import pomodoroImage from "@/assets/blog/pomodoro-technique.jpg";
import taskPlanningImage from "@/assets/blog/task-planning.jpg";
import studyCommunityImage from "@/assets/blog/study-community.jpg";

const tocItems = [
  { id: "understanding-procrastination", title: "Understanding Procrastination" },
  { id: "making-a-promise", title: "First Step: Making a Promise to Yourself" },
  { id: "breaking-it-down", title: "Second Step: Breaking it Down" },
  { id: "motivation-and-energy", title: "Third Step: Motivation and Mental Energy" },
  { id: "conclusion", title: "Conclusion" },
];

const HowToStopProcrastination = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO 
        title="How to Stop Procrastination"
        description="Learn practical strategies to overcome procrastination. Understand why you procrastinate and discover gentle, effective techniques to reclaim your time and focus."
        keywords="stop procrastination, overcome procrastination, productivity tips, focus techniques, time management, pomodoro technique, CSW, online study rooms"
        canonicalUrl="/blog/how-to-stop-procrastination/"
      />
      <Header />
      <BlogReadingProgress />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-background to-background" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-secondary/10 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <Link 
              to="/blog/" 
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-10 font-body"
              onClick={() => window.scrollTo(0, 0)}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
            
            <div className="flex flex-wrap items-center gap-4 mb-8">
              <span className="px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium font-body">
                Productivity
              </span>
              <div className="flex items-center gap-2 text-muted-foreground text-sm font-body">
                <Calendar className="w-4 h-4" />
                <span>December 16, 2024</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground text-sm font-body">
                <Clock className="w-4 h-4" />
                <span>10 min read</span>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-10 leading-[1.1] tracking-tight">
              How to Stop Procrastination
            </h1>

            {/* Featured Image */}
            <img 
              src={heroImage} 
              alt="Productivity and focus - organized desk with completed checklist at sunrise" 
              className="w-full aspect-video object-cover rounded-2xl mb-8"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="pb-24 relative">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Main Content */}
            
              {/* Lead Paragraph */}
              <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed font-body mb-10">
                Procrastination is something all of us have faced at one time or another. It's that quiet, sneaky voice that oftentimes convinces us to put off what we need to work on for tomorrow. Whether it's delaying a work project, ignoring a workout, or scrolling endlessly through our phones, procrastination can feel like a frustrating and stubborn companion.
              </p>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                What most people think of procrastination is laziness or lack of willpower. But it's not. It's actually an emotional phenomenon between what needs to get done and our natural desire to avoid discomfort.
              </p>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                So, let me tell you what this article won't do. It won't be about quick fixes or harsh self-criticism. Instead, it'll be an exploration of why you're procrastinating and how to stop this cycle without magically summoning endless willpower or reorganizing your entire life overnight. We'll explore then on how to gently nudge yourself past procrastination, making progress feel less like a chore and more like a natural part of your day.
              </p>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                Let's go ahead and dive in on how to quietly reclaim your time and focus without making it overwhelming.
              </p>

              {/* Mobile TOC */}
              <div className="lg:hidden mb-12">
                <BlogTableOfContents items={tocItems} />
              </div>

            {/* Section 1 */}
            <h2 id="understanding-procrastination" className="text-3xl md:text-4xl font-display font-bold mt-16 mb-8 tracking-tight">
              Understanding Procrastination
            </h2>

            {/* Understanding Procrastination Image */}
            <img 
              src={taskPlanningImage} 
              alt="Organized workspace with planner, sticky notes, and to-do list showing productivity tools" 
              className="w-full aspect-video object-cover rounded-xl mb-10"
            />

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              Before we learn how to stop procrastination, it's important to understand what the hell procrastination is. Procrastination isn't just a matter of being lazy or unmotivated, it's actually much deeper than what most people think. As <a href="https://jamesclear.com/how-to-stop-procrastinating" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">James Clear explains</a>, procrastination is simply our mind trying to protect ourselves from the uncomfortable feelings like fear of failure, self-doubt, or even feeling overwhelmed by how big a task might seem. Procrastination is simply the mental tug-of-war between wanting to get something done and wanting to avoid the stress that comes with it.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
              Most of us have been there. Staring at a project that feels too daunting or putting off something at the very last minute. The truth is, as <a href="https://greatergood.berkeley.edu/article/item/two_Counterintuitive_ways_to_stop_procrastinating" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">research from UC Berkeley's Greater Good Science Center</a> suggests, procrastination is a common coping mechanism. It's our brain's way of managing things when we feel overwhelmed. Understanding this is crucial when it comes to stopping procrastination. Because instead of approaching it with frustration, like others usually do, you can instead approach it more holistically.
            </p>

            {/* Section 2 */}
            <h2 id="making-a-promise" className="text-3xl md:text-4xl font-display font-bold mt-16 mb-8 tracking-tight">
              First Step: Making a Promise to Yourself
            </h2>

            <div className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10">
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Target className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h3 className="text-2xl font-display font-bold mb-4">Commitment Creates Accountability</h3>
                  <p className="text-lg leading-relaxed font-body text-muted-foreground">
                    The first actual step that you need to take is to make a promise to yourself. It's surprisingly a powerful way when it comes to overcoming procrastination. When you start making promises to yourself, it shouldn't be just internally saying "I'll get this done someday." It should be instead consciously committing to a goal.
                  </p>
                </div>
              </div>
            </div>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              The first thing you have to do though is that you shouldn't just say this to yourself. Instead, you should write it down or tell a friend and even set a clear deadline. I generally like to write it in post-it notes that I can see every time or tell my friends about it. This is to ensure that you create a sense of accountability that helps keep you accountable. <a href="https://academicsupport.jhu.edu/resources/study-aids/overcoming-procrastination/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Johns Hopkins University's academic support resources</a> also emphasize the power of external accountability in overcoming procrastination.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              I mean think about the times where you've made plans with a friend or set internal deadlines for a group project, somehow, that outside commitment often pushes you to follow through. These little promises anchor your intentions and transform vague "maybe someday" ideas into something real and actionable. The key is to start with commitments that feel doable, building your confidence and motivation one step at a time.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
              Okay, so what if you started making a promise to yourself? That doesn't mean you won't feel overwhelmed about it. Well, that's where our step two comes in.
            </p>

            {/* Section 3 */}
            <h2 id="breaking-it-down" className="text-3xl md:text-4xl font-display font-bold mt-16 mb-8 tracking-tight">
              Second Step: Breaking it Down
            </h2>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              As previously mentioned, one of the biggest reasons procrastination takes a hold of you is because tasks can feel overwhelming. It's like a mountain too high to climb all at once. But here's the thing, as <a href="https://www.boisestate.edu/coen-mbe/2021/04/20/5-ways-to-stop-procrastinating/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Boise State University researchers suggest</a>, the secret to climbing that mountain isn't about pushing harder, instead it's about breaking things down into smaller, manageable steps. Doing this makes work from a daunting chore into a series of clear and achievable steps.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-10">
              But how exactly do you do that? Well, start by taking any large task and slicing it into bite-size chunks. Instead of telling yourself "I need to write a report by Friday" try breaking it down into specific actions like "making an outline first", "writing an introduction" or "editing the first draft." You can even go further by setting milestones or goals for each task you accomplish.
            </p>

            {/* Step Cards */}
            <div className="space-y-6 mb-10">
              <div className="flex gap-6">
                <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display font-bold text-xl flex-shrink-0">
                  1
                </div>
                <div>
                  <h3 className="font-display font-bold text-xl mb-3">Don't Overdo It</h3>
                  <p className="text-lg leading-relaxed font-body text-muted-foreground">
                    While making any large task into bite-size chunks is great, overdoing it can instead be counterproductive. You'll likely feel more overwhelmed instead of actually seeing progress.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display font-bold text-xl flex-shrink-0">
                  2
                </div>
                <div>
                  <h3 className="font-display font-bold text-xl mb-3">Use Time-Blocking or the Pomodoro Technique</h3>
                  <p className="text-lg leading-relaxed font-body text-muted-foreground">
                    This is basically where you dedicate about 25 minutes to one task, followed by a short break like 5 minutes. This method not only keeps your mind fresh but also creates a rhythm that encourages steady progress while at the same time, rewarding yourself. I generally use <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">CSW's built-in pomodoro timer</a> which lets you adjust your focus time and short break to your own preferences. Aside from that, being able to see progress is clearly important. Good thing that CSW has a progress tracker that lets you know where you are with your tasks! Check out our <Link to="/study-timer/" className="text-primary hover:underline" onClick={() => window.scrollTo(0, 0)}>study timer tools</Link> to find the best one for you.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display font-bold text-xl flex-shrink-0">
                  3
                </div>
                <div>
                  <h3 className="font-display font-bold text-xl mb-3">Prepare Your Workspace</h3>
                  <p className="text-lg leading-relaxed font-body text-muted-foreground">
                    Preparing your workspace the night before can make a big difference. Clear away distractions, gather what you need, and create a specific plan for what to tackle first. Having this structure in place can reduce the mental friction that comes with deciding where to start.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display font-bold text-xl flex-shrink-0">
                  4
                </div>
                <div>
                  <h3 className="font-display font-bold text-xl mb-3">Prioritize Your Tasks</h3>
                  <p className="text-lg leading-relaxed font-body text-muted-foreground">
                    Focus on what matters most. If everything feels urgent, try choosing just one or two "big rocks" for the day. Choose the two few important tasks that will move you forward the most. When you organize your time around these meaningful priorities, it's easier to maintain momentum and feel a real sense of accomplishment.
                  </p>
                </div>
              </div>
            </div>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
              By breaking work into clear steps, organizing your hours, and setting realistic goals, procrastination loses much of its power. It's about creating a steady, manageable flow that fits naturally into your day, helping you progress with less stress and more confidence.
            </p>

            {/* Breaking Tasks Image */}
            <img 
              src={pomodoroImage} 
              alt="Pomodoro timer technique - tomato timer with 25 minutes on study desk" 
              className="w-full aspect-[16/9] object-cover rounded-xl my-12"
            />

            {/* Section 4 */}
            <h2 id="motivation-and-energy" className="text-3xl md:text-4xl font-display font-bold mt-16 mb-8 tracking-tight">
              Third Step: Motivation and Mental Energy
            </h2>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              Willpower often gets a bad reputation. It's this mysterious, endless force we either have or don't. The truth is, as <a href="https://www.melrobbins.com/episode/episode-42/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">motivation expert Mel Robbins discusses</a>, willpower is more like a muscle that eventually gets tired if you don't keep strengthening it with the right care. The key to overcoming procrastination then is managing your mental energy thoughtfully as well as being kind to yourself along the way.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-10">
              When faced with a task you've been procrastinating, you might notice feelings like frustration, self-doubt, or even anxiety that surfaces. Instead of battling those emotions head-on or beating yourself up for procrastinating, try to acknowledge them without judgment instead. Practicing mindfulness, like simply noticing how you feel without or how you're reacting creates a space for you to process and analyze your thoughts. Doing this will hopefully help you understand what your next actions should be as well as help reduce the urge to avoid the task.
            </p>

            {/* Motivation Cards */}
            <div className="grid md:grid-cols-2 gap-6 mb-10">
              <div className="bg-card border border-border rounded-2xl p-6 hover:border-primary/30 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <Zap className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-display font-bold mb-3">Start Small</h3>
                <p className="text-muted-foreground font-body">
                  If you don't have anything urgent, then starting small is completely fine. Personally, I like the feeling of being able to cross something out of my planner and seeing it crossed throughout the day pushes me to work.
                </p>
              </div>

              <div className="bg-card border border-border rounded-2xl p-6 hover:border-primary/30 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <CheckCircle className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-display font-bold mb-3">Track Your Progress</h3>
                <p className="text-muted-foreground font-body">
                  That's why I tend to write everything I did in my planner even though I already did it just to cross it later. The visual satisfaction of seeing tasks completed motivates continued progress.
                </p>
              </div>
            </div>

            <div className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10">
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Brain className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h3 className="text-2xl font-display font-bold mb-4">The Power of Online Study Rooms</h3>
                  <p className="text-lg leading-relaxed font-body text-muted-foreground mb-4">
                    One surprisingly effective way to boost willpower is by using <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">24/7 online study rooms</a> or virtual workspace. These virtual rooms create a unique environment which for me taps into the idea of the panopticon.
                  </p>
                  <p className="text-lg leading-relaxed font-body text-muted-foreground mb-4">
                    So the panopticon is basically a design originally used in prisons where one watchtower can observe everyone. Sounds scary right? But stay with me. Because the panopticon is designed in such a way that literally everyone can see you, you become more aware of your surroundings.
                  </p>
                  <p className="text-lg leading-relaxed font-body text-muted-foreground">
                    In an online study room, although no one is physically observing you, the idea that someone could be watching you plus the fact that others are also being productive encourages you to focus. That feeling of others "watching" my progress really pushes me to work more.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-12">
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Heart className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h3 className="text-2xl font-display font-bold mb-4">Be Gentle With Yourself</h3>
                  <p className="text-lg leading-relaxed font-body text-muted-foreground mb-4">
                    Aside from that, remember to be gentle with yourself! This is not about making excuses but rather about creating an environment where your mental energy thrives! Take breaks, and occasionally self-indulge yourself (don't do it too much though! Make sure you earned it).
                  </p>
                  <p className="text-lg leading-relaxed font-body text-muted-foreground">
                    These are essential tools that keep your willpower strong and sustainable in the long run! By becoming more kind to yourself and by managing your mental health more, it makes it easier for you to focus and procrastination becomes nothing more than something your past self used to do.
                  </p>
                </div>
              </div>
            </div>

            {/* Conclusion */}
            <h2 id="conclusion" className="text-3xl md:text-4xl font-display font-bold mt-16 mb-8 tracking-tight">
              Conclusion
            </h2>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              Hopefully this article made you realize unique ways to stop procrastinating. Stopping it isn't just about sudden, dramatic changes or perfect self-control but instead a journey of small and intentional steps. For more practical tips, <a href="https://www.theguardian.com/wellness/2025/oct/28/how-to-stop-procrastinating-tips" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">The Guardian's wellness guide</a> offers additional strategies worth exploring.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
              As each intentional step, though small, quietly build momentum towards a more fulfilling daily routine! With consistency and compassion, procrastination loses its grip, and your goals become easy to reach!
            </p>

            {/* CTA Section */}
            <div className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-2xl p-8 md:p-12 text-center">
              <Timer className="w-12 h-12 text-primary mx-auto mb-6" />
              <h3 className="text-2xl md:text-3xl font-display font-bold mb-4">
                Ready to Stop Procrastinating?
              </h3>
              <p className="text-lg text-muted-foreground font-body mb-8 max-w-xl mx-auto">
                Join thousands of students using CSW's free online study rooms and productivity tools to stay focused and accountable.
              </p>
              <a 
                href="https://app.csw.live" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 bg-primary text-primary-foreground rounded-xl font-semibold text-lg hover:bg-primary/90 transition-colors"
              >
                Try CSW for Free
              </a>
            </div>

            {/* Related Articles Section */}
            <div className="mt-20">
              <h2 className="text-3xl md:text-4xl font-display font-bold mb-10 tracking-tight">
                Related Articles
              </h2>
              <div className="grid md:grid-cols-1 gap-6">
                <Link 
                  to="/blog/how-turning-on-my-camera-boosted-my-productivity/"
                  onClick={() => window.scrollTo(0, 0)}
                  className="group bg-card border border-border rounded-2xl overflow-hidden hover:border-primary/30 transition-all"
                >
                  <div className="flex flex-col md:flex-row">
                    <div className="md:w-1/3">
                      <img 
                        src={studyCommunityImage} 
                        alt="Virtual study community" 
                        className="w-full h-48 md:h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-6 md:w-2/3 flex flex-col justify-center">
                      <span className="text-sm text-primary font-medium font-body mb-2">Productivity</span>
                      <h3 className="text-xl md:text-2xl font-display font-bold mb-3 group-hover:text-primary transition-colors">
                        How Turning on my Camera Boosted my Productivity
                      </h3>
                      <p className="text-muted-foreground font-body line-clamp-2">
                        Discover how turning on your camera in online study rooms can transform your productivity through the power of accountability and community.
                      </p>
                    </div>
                  </div>
                </Link>
              </div>
            </div>

          </div>
          
          {/* Floating Sticky TOC - Desktop only */}
          <aside className="hidden lg:block fixed right-8 xl:right-16 top-24 z-40">
            <BlogTableOfContents items={tocItems} collapsible />
          </aside>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default HowToStopProcrastination;